import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { LayoutRequest, LayoutResponse, ProjectRead, ProjectDetail, Token, User, TrackerEquipmentRead, ModuleEquipmentRead } from '../types';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Token storage ────────────────────────────────────────────────────────────
// Tokens are stored in memory (not localStorage) to prevent XSS exfiltration.
// The access token is kept in a closure; the refresh token in sessionStorage
// (survives page refresh within the tab but not across tabs/windows).

let _accessToken: string | null = null;

export const tokenStore = {
  getAccess: () => _accessToken,
  setAccess: (t: string | null) => { _accessToken = t; },
  getRefresh: () => sessionStorage.getItem('sf_rt'),
  setRefresh: (t: string | null) => {
    if (t) sessionStorage.setItem('sf_rt', t);
    else sessionStorage.removeItem('sf_rt');
  },
  clear: () => {
    _accessToken = null;
    sessionStorage.removeItem('sf_rt');
  },
};

// ─── Axios instance ───────────────────────────────────────────────────────────

const http: AxiosInstance = axios.create({ baseURL: BASE_URL, timeout: 120_000 }); // 120s — large sites with NetworkX routing can take 30-60s

// Attach access token to every request
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, attempt silent token refresh then retry once
let _refreshing = false;
let _waiters: Array<(token: string | null) => void> = [];

http.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status !== 401 || original._retry) return Promise.reject(err);

    original._retry = true;

    if (_refreshing) {
      return new Promise((resolve, reject) => {
        _waiters.push((token) => {
          if (token) {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(http(original));
          } else {
            reject(err);
          }
        });
      });
    }

    _refreshing = true;
    const rt = tokenStore.getRefresh();

    if (!rt) {
      _refreshing = false;
      tokenStore.clear();
      window.dispatchEvent(new Event('sf:logout'));
      return Promise.reject(err);
    }

    try {
      const res = await axios.post<Token>(`${BASE_URL}/auth/refresh`, {
        refresh_token: rt,
      });
      const { access_token, refresh_token } = res.data;
      tokenStore.setAccess(access_token);
      tokenStore.setRefresh(refresh_token);
      _waiters.forEach((cb) => cb(access_token));
      _waiters = [];
      original.headers.Authorization = `Bearer ${access_token}`;
      return http(original);
    } catch {
      tokenStore.clear();
      _waiters.forEach((cb) => cb(null));
      _waiters = [];
      window.dispatchEvent(new Event('sf:logout'));
      return Promise.reject(err);
    } finally {
      _refreshing = false;
    }
  }
);

// ─── Auth endpoints ───────────────────────────────────────────────────────────

export const authApi = {
  register: (email: string, password: string, full_name = '') =>
    http.post<User>('/auth/register', { email, password, full_name }).then((r) => r.data),

  login: async (email: string, password: string): Promise<User> => {
    const { data: tokens } = await http.post<Token>('/auth/login', { email, password });
    tokenStore.setAccess(tokens.access_token);
    tokenStore.setRefresh(tokens.refresh_token);
    const { data: user } = await http.get<User>('/auth/me');
    return user;
  },

  logout: async () => {
    try { await http.post('/auth/logout'); } catch {}
    tokenStore.clear();
  },

  me: () => http.get<User>('/auth/me').then((r) => r.data),
};

// ─── Layout endpoints ─────────────────────────────────────────────────────────

export const layoutApi = {
  generate: (req: LayoutRequest) =>
    http.post<LayoutResponse>('/layout', req).then((r) => r.data),

  defaults: () =>
    http.get<LayoutRequest>('/layout/defaults').then((r) => r.data),
};

// ─── Project endpoints ────────────────────────────────────────────────────────

export const projectApi = {
  create: (req: LayoutRequest, name = 'Unnamed project') =>
    http
      .post<ProjectRead>(`/projects?name=${encodeURIComponent(name)}`, req)
      .then((r) => r.data),

  list: (skip = 0, limit = 50) =>
    http.get<ProjectRead[]>(`/projects?skip=${skip}&limit=${limit}`).then((r) => r.data),

  get: (id: string) =>
    http.get<ProjectDetail>(`/projects/${id}`).then((r) => r.data),

  rename: (id: string, name: string) =>
    http
      .patch<ProjectRead>(`/projects/${id}?name=${encodeURIComponent(name)}`)
      .then((r) => r.data),

  delete: (id: string) => http.delete(`/projects/${id}`),
};
// ─── Billing endpoints ────────────────────────────────────────────────────────

export const billingApi = {
  checkout: (priceId: string) =>
    http
      .post<{ url: string }>(
        `/billing/checkout?price_id=${priceId}` +
        `&success_url=${encodeURIComponent(window.location.origin + '/billing/success')}` +
        `&cancel_url=${encodeURIComponent(window.location.origin)}`
      )
      .then((r) => r.data),

  portal: () =>
    http
      .post<{ url: string }>(
        `/billing/portal?return_url=${encodeURIComponent(window.location.origin)}`
      )
      .then((r) => r.data),

  plans: () => http.get<Record<string, unknown>>('/billing/plans').then((r) => r.data),
};

// ─── Equipment endpoints ──────────────────────────────────────────────────────

export const equipmentApi = {
  trackers: (trackerType?: string) => {
    const q = trackerType ? `?tracker_type=${trackerType}` : '';
    return http.get<TrackerEquipmentRead[]>(`/equipment/trackers${q}`).then((r) => r.data);
  },

  tracker: (id: string) =>
    http.get<TrackerEquipmentRead>(`/equipment/trackers/${id}`).then((r) => r.data),

  modules: (cellType?: string, bifacial?: boolean) => {
    const params = new URLSearchParams();
    if (cellType  !== undefined) params.set('cell_type', cellType);
    if (bifacial  !== undefined) params.set('bifacial', String(bifacial));
    const q = params.toString() ? `?${params.toString()}` : '';
    return http.get<ModuleEquipmentRead[]>(`/equipment/modules${q}`).then((r) => r.data);
  },

  module: (id: string) =>
    http.get<ModuleEquipmentRead>(`/equipment/modules/${id}`).then((r) => r.data),
};